/**
 * Project Bobcat — structure for the Parameter Input field's drill-down.
 *
 * Pure and dependency-light on purpose (same discipline as `node-field-groups.ts`):
 * every branch is unit-testable without a store, a row, or React.
 *
 * ## Why this is schema-AGNOSTIC, not a fixed field list per section
 *
 * No Zod/TS schema exists anywhere in the monorepo for this column — the PIF
 * compiler treats it as an opaque JSON blob it round-trips
 * (`prepare-rows-data-for-pif-compiler.ts`) — and it turns out there isn't
 * ONE real shape to model. Checked against TWO real, currently-live nodes in
 * a real Pro Studio project (not the design prototype's mockup data, and not
 * only the AI-Native CSV template this module originally modelled):
 *
 * - An older/currently-common shape: `chat_context_setting` (system prompt),
 *   `user_utterance` (user prompt), `safety_settings` (an array of
 *   `{category, threshold}`), `parameters.temperature` /
 *   `max_output_tokens` / `top_k` / `top_p` / `thinking_config`.
 * - The AI-Native CSV template's shape: `system_prompt`, `user_prompt`,
 *   `tracing`, `mcp`, `parameters.max_tokens` / `reasoning_effort`.
 *
 * Both are real; a first pass at this module hard-coded field lists for only
 * the second and would have shown "Not set" for every field on any node
 * using the first — silently useless for exactly the nodes most likely to
 * exist in a real project today. So a SECTION here only ever claims TOP-LEVEL
 * keys (which concept a key belongs to: prompts, retrieval, model/generation
 * config, tracing, mcp, response contract, routing, wiring) — never a
 * specific inner key name. What fields actually show inside a section is
 * whatever keys `data` actually has under it, generically labelled and typed
 * off their OWN value (`inferFieldKind`) — string, number, or (object/array)
 * JSON. That degrades gracefully to a THIRD shape nobody has seen yet, the
 * same way it already covers both shapes seen so far.
 *
 * A real `parameterInput` value only ever carries SOME of these top-level
 * keys — a simple action node can be just `{"save_error_to":
 * "PLATFORM_ERROR"}`, with none of the recognised sections present at all.
 * `detectSections` returns only the sections a given value actually has data
 * for; the "Raw JSON" escape hatch is always available regardless, so
 * nothing in an unrecognised or partially-recognised shape is ever
 * unreachable through this UI.
 */

export type ParameterInputSectionId =
  | 'prompts'
  | 'retrieval'
  | 'model'
  | 'tracing'
  | 'mcp'
  | 'contract'
  | 'routing'
  | 'wiring'
  | 'other';

export interface ParameterInputSectionDef {
  id: ParameterInputSectionId;
  label: string;
  /** Top-level keys this section claims. A section is "present" when the
   *  parsed value has at least one of these as an own key. Which keys show
   *  as FIELDS inside it is derived from `data` itself, not from this list —
   *  see the module doc comment. */
  keys: string[];
}

/**
 * Display order matches the prototype's own ordering, which followed a
 * natural read order (what the node says → what it retrieves → how it
 * generates → how it's tracked/wired) rather than any structural fact — kept
 * for continuity since there is nothing else to derive it from.
 *
 * `model`'s keys include `safety_settings` — a sibling "how the LLM
 * behaves" concept, grouped with `model`/`parameters` rather than given its
 * own section since a solution only ever has ONE generation config.
 */
export const PARAMETER_INPUT_SECTIONS: readonly ParameterInputSectionDef[] = [
  {
    id: 'prompts',
    label: 'Prompts',
    keys: [
      'system_prompt',
      'user_prompt',
      'chat_context_setting',
      'user_utterance',
    ],
  },
  {
    id: 'retrieval',
    label: 'Knowledge retrieval',
    keys: [
      'sandbox_corpus_id',
      'live_corpus_id',
      'input_var',
      'save_output_as',
    ],
  },
  {
    id: 'model',
    label: 'Model',
    keys: ['model', 'parameters', 'safety_settings'],
  },
  { id: 'tracing', label: 'Tracing', keys: ['tracing'] },
  { id: 'mcp', label: 'MCP', keys: ['mcp'] },
  {
    id: 'contract',
    label: 'Response contract',
    keys: ['save_llm_response_as'],
  },
  { id: 'routing', label: 'Routing', keys: ['destination_nodes'] },
  { id: 'wiring', label: 'Variable wiring', keys: ['set'] },
];

const ALL_KNOWN_KEYS = new Set(
  PARAMETER_INPUT_SECTIONS.flatMap((section) => section.keys)
);

export type ParsedParameterInput = Record<string, unknown>;

export interface ParseResult {
  /** `null` when the raw string is not a JSON object — see `parseParameterInput`. */
  data: ParsedParameterInput | null;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Parses `raw` for the drill-down. Anything that isn't a JSON OBJECT — invalid
 * JSON, or valid JSON that parses to an array/string/number — falls back to
 * `data: null`, which the field component reads as "show the plain textarea,
 * same as before this feature existed." A bare array or scalar is valid JSON
 * for this column (the PIF compiler never required an object), but there is
 * no sensible section to file it under.
 */
export const parseParameterInput = (raw: string): ParseResult => {
  if (!raw.trim()) return { data: null };
  try {
    const parsed = JSON.parse(raw);
    return { data: isPlainObject(parsed) ? parsed : null };
  } catch {
    return { data: null };
  }
};

/** Sections `data` actually has content for, in display order. A key present
 *  with an empty-string/undefined value still counts — the row exists, so
 *  the section stays reachable to clear or fill it in. */
export const detectSections = (
  data: ParsedParameterInput
): ParameterInputSectionId[] =>
  PARAMETER_INPUT_SECTIONS.filter((section) =>
    section.keys.some((key) => key in data)
  ).map((section) => section.id);

/** Top-level keys not claimed by any known section — the "Other" bucket.
 *  A key here is real data (e.g. `save_error_to`), never invented. */
export const otherKeys = (data: ParsedParameterInput): string[] =>
  Object.keys(data).filter((key) => !ALL_KNOWN_KEYS.has(key));

export type FieldPath = readonly [string] | readonly [string, string];

/**
 * The fields to render for one section, derived from whichever of its
 * claimed top-level keys `data` actually has — NOT a fixed list (see the
 * module doc comment for why). A top-level key whose value is a plain
 * object gets ONE level of generic recursion (one field per ITS own key —
 * this is how `parameters`/`tracing`/`mcp` turn into several fields from a
 * single claimed key); anything deeper, or an array, is left whole for
 * `inferFieldKind` to render as a single JSON field rather than guessing
 * further into a shape nothing here has validated.
 */
export const fieldPathsForSection = (
  data: ParsedParameterInput,
  sectionId: ParameterInputSectionId
): FieldPath[] => {
  const section = PARAMETER_INPUT_SECTIONS.find((s) => s.id === sectionId);
  if (!section) return [];

  const paths: FieldPath[] = [];
  section.keys.forEach((key) => {
    if (!(key in data)) return;
    const value = data[key];
    if (isPlainObject(value)) {
      Object.keys(value).forEach((subKey) => paths.push([key, subKey]));
    } else {
      paths.push([key]);
    }
  });
  return paths;
};

/**
 * Merges the "Other fields" textarea's edited leftover-key object back into
 * `data`, preserving every OTHER key's original position — including the
 * previously-leftover keys themselves, not just the known-section ones.
 *
 * A naive `{ ...withoutOldLeftover, ...parsedOther }` (dropping the leftover
 * keys first, then re-adding whatever the textarea now has) moves every
 * leftover key to the END of the object on every edit, even one that changed
 * nothing about their position — `object spread` only preserves the position
 * of keys it never touches, and this drops them before re-adding them. This
 * instead walks `data` in its EXISTING order, keeping each leftover key in
 * place if the edited object still has it (with its new value), dropping it
 * if the edit removed it, and appending only genuinely NEW keys the edit
 * introduced — which have no prior position to preserve.
 */
export const mergeOtherKeys = (
  data: ParsedParameterInput,
  leftover: readonly string[],
  parsedOther: ParsedParameterInput
): ParsedParameterInput => {
  const leftoverSet = new Set(leftover);
  const remaining = { ...parsedOther };
  const result: ParsedParameterInput = {};

  Object.entries(data).forEach(([key, value]) => {
    if (!leftoverSet.has(key)) {
      result[key] = value;
      return;
    }
    if (key in remaining) {
      result[key] = remaining[key];
      delete remaining[key];
    }
    // else: this leftover key was removed in the edited textarea — dropped.
  });

  // Whatever's left in `remaining` is a brand-new key the edit introduced —
  // it has no earlier position in `data` to preserve, so it goes last.
  return { ...result, ...remaining };
};

/** `data` re-serialized. Plain `JSON.stringify` — the store's own
 *  `getJsonOrderMap` re-derives its column-order map by re-parsing whatever
 *  string this produces, so nothing here needs to hand-preserve key order;
 *  object spread already keeps every UNTOUCHED key exactly where it was. */
export const serializeParameterInput = (data: ParsedParameterInput): string =>
  JSON.stringify(data, null, 2);

/**
 * Immutable set of a possibly-nested field, addressed by a `[key]` or
 * `[key, subKey]` path. Only ever mutates the ONE path given — every other
 * key, however deep, is carried over untouched via spread, so an edit to
 * `parameters.max_output_tokens` cannot disturb `model` or anything outside
 * `parameters`.
 */
export const setParameterInputPath = (
  data: ParsedParameterInput,
  path: FieldPath,
  value: unknown
): ParsedParameterInput => {
  if (path.length === 1) {
    return { ...data, [path[0]]: value };
  }
  const [key, subKey] = path;
  const existing = data[key];
  const nested = isPlainObject(existing) ? existing : {};
  return { ...data, [key]: { ...nested, [subKey]: value } };
};

/** Reads a `[key]` or `[key, subKey]` path. */
export const getParameterInputPath = (
  data: ParsedParameterInput,
  path: FieldPath
): unknown => {
  if (path.length === 1) return data[path[0]];
  const [key, subKey] = path;
  const nested = data[key];
  return isPlainObject(nested) ? nested[subKey] : undefined;
};

/** How a value's own field should be edited — inferred from the value
 *  itself, not from a hard-coded key name, so a field renders sensibly
 *  whichever of the real shapes (or a future one) it came from. Objects and
 *  arrays fall back to JSON rather than being decomposed further — see the
 *  module doc comment on the one-level recursion limit. */
export type InferredFieldKind = 'text' | 'textarea' | 'number' | 'json';

export const inferFieldKind = (value: unknown): InferredFieldKind => {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string') {
    return value.length > 80 || value.includes('\n') ? 'textarea' : 'text';
  }
  return 'json';
};

/** Turns a `snake_case` (or already-Title-Case) key into a readable label —
 *  generic on purpose, so a key this module has never seen still gets a
 *  reasonable label instead of showing nothing until code catches up. */
export const labelForKey = (key: string): string =>
  key
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/** One-line summary shown on the section's row in the list view: the first
 *  non-empty field's value, generic over whichever keys are actually
 *  present (see `fieldPathsForSection`). */
export const summarizeSection = (
  data: ParsedParameterInput,
  sectionId: ParameterInputSectionId
): string => {
  const paths = fieldPathsForSection(data, sectionId);
  for (const path of paths) {
    const value = getParameterInputPath(data, path);
    if (value == null || value === '') continue;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    try {
      const json = JSON.stringify(value);
      if (Array.isArray(value)) {
        return `${value.length} item${value.length === 1 ? '' : 's'}`;
      }
      return json;
    } catch {
      continue;
    }
  }
  return 'Not set';
};
